/**
 * Separate axios instance for Admin RBAC (FROZEN backend contract R1–R9, M2 S2.1.1).
 *
 * - `baseURL: API_BASE_URL`, `withCredentials: true` (httpOnly refresh cookie).
 * - Request: device headers (Device-Id / Device-Type, mirrored from
 *   `src/services/api.service.ts`) + memory-only `Authorization: Bearer <token>`
 *   via the zustand auth store. The store is reached through a LAZY
 *   `await import()` inside the interceptor — NEVER a static import, because
 *   `usePermStore.ts` statically imports this module (cycle ban).
 * - Response 401 (unauthenticated): single-flight `POST admin.refresh`
 *   (cookie-only, plain axios to avoid re-entering interceptors) → retry the
 *   original request once with the new token → on refresh failure clear the
 *   memory session + redirect `/admin/login?next=<pathname>`. No detail toast.
 * - Response 403 (authenticated but forbidden/disabled): do NOT logout;
 *   generic `Action not permitted` toast (never enumerates the missing perm);
 *   `disabled` → `/admin/disabled`, else → `/admin/no-access`.
 * - Refresh-token fallback reads go through `@/admin/auth/tokenStorage`
 *   (persisted token another tab rotated in) — never raw localStorage.
 */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from '@/config/api.config';
import { toast } from '@/components/ui/use-toast';
import { deviceService } from '@/services/device.service';
import { logger } from '@/services/logger.service';

/** Swappable redirect sink — S5.2.1 tests spy on `adminRedirects.to`. */
export const adminRedirects = {
  to(path: string): void {
    if (typeof window !== 'undefined' && window.location.pathname !== path) {
      window.location.href = path;
    }
  },
};

export const adminClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json; charset=UTF-8',
  },
});

/** Memory-only `Bearer <token>` header, or null when logged out / store unavailable. */
async function getMemoryAuthHeader(): Promise<string | null> {
  try {
    // LAZY import: usePermStore statically imports adminClient, so a static
    // import of either store here would create a module cycle.
    const { getAuthHeader } = await import('@/admin/auth/useAuthStore');
    return getAuthHeader();
  } catch {
    return null;
  }
}

/** Best-effort persist of a refreshed token, preserving the cached admin. */
async function persistRefreshedToken(token: string, rotatedRefreshToken?: string | null): Promise<void> {
  try {
    const { useAuthStore } = await import('@/admin/auth/useAuthStore');
    const st = useAuthStore.getState();
    const newRt = rotatedRefreshToken ?? st.refreshToken;
    if (st.admin) {
      st.setSession(token, st.admin, newRt);
    } else {
      // No identity yet (boot path) — still persist both tokens directly.
      writePersistedAccessToken(token);
    }
    // Persist rotated refresh token for reload/new-tab survival.
    writePersistedRefreshToken(newRt);
  } catch {
    // Store unavailable — retry still carries the new token.
  }
}

async function clearMemorySession(): Promise<void> {
  try {
    const { useAuthStore } = await import('@/admin/auth/useAuthStore');
    useAuthStore.getState().clearSession();
  } catch {
    // No-op when the store is unavailable (isolated tests).
  }
}

import {
  readPersistedRefreshToken,
  writePersistedAccessToken,
  writePersistedRefreshToken,
} from '@/admin/auth/tokenStorage';

interface RefreshResponse {
  access_token?: string;
  refresh_token?: string;
}

/** Single refresh attempt. Plain axios (NOT adminClient) — avoids re-entering interceptors. */
async function tryRefresh(body: Record<string, string>): Promise<RefreshResponse | null> {
  try {
    const res = await axios.post<RefreshResponse>(
      `${API_BASE_URL}${API_ENDPOINTS.admin.refresh}`,
      body,
      { withCredentials: true },
    );
    return res.data ?? null;
  } catch {
    return null;
  }
}

/** Single-flight refresh promise shared across concurrent 401s. */
let refreshPromise: Promise<string | null> | null = null;

function doRefresh(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        // Backend single-use-rotates refresh tokens, and all tabs share one
        // persisted token: a sibling tab may have rotated since this tab
        // last wrote. Candidates: memory first, then persisted. On failure,
        // re-read storage once (rotation may have landed mid-attempt).
        let memoryRt: string | null = null;
        try {
          const { useAuthStore } = await import('@/admin/auth/useAuthStore');
          memoryRt = useAuthStore.getState().refreshToken;
        } catch {
          // Store unavailable — persisted fallback below still applies.
        }
        const tried = new Set<string>();
        const candidates = [memoryRt, readPersistedRefreshToken()].filter(
          (rt): rt is string => !!rt && !tried.has(rt) && (tried.add(rt), true),
        );
        for (const rt of candidates) {
          const data = await tryRefresh({ refresh_token: rt });
          if (data?.access_token) {
            await persistRefreshedToken(data.access_token, data.refresh_token ?? rt);
            return data.access_token;
          }
        }
        // A concurrent tab may have rotated between our read and attempt.
        const fresh = readPersistedRefreshToken();
        if (fresh && !tried.has(fresh)) {
          const data = await tryRefresh({ refresh_token: fresh });
          if (data?.access_token) {
            await persistRefreshedToken(data.access_token, data.refresh_token ?? fresh);
            return data.access_token;
          }
        }
        // Legacy last resort: historically attempted with an empty body.
        // No cookie is ever set by the backend, so this 401s — kept only
        // to preserve the previous attempt contract.
        const legacy = await tryRefresh({});
        if (legacy?.access_token) {
          await persistRefreshedToken(legacy.access_token, legacy.refresh_token ?? null);
          return legacy.access_token;
        }
        logger.error('Admin token refresh failed: all candidates rejected');
        return null;
      } catch (e) {
        logger.error('Admin token refresh failed:', e);
        return null;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

/** Auth endpoints must never trigger a refresh loop. */
function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return url.includes('/auth/admin/refresh') || url.includes('/auth/admin/login');
}

function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  adminRedirects.to(`/admin/login?next=${next}`);
}

interface RetriableConfig extends AxiosRequestConfig {
  _retry?: boolean;
  /**
   * Marks an OPTIONAL sub-resource call (a panel/tab inside an otherwise
   * allowed page). A 403 on such a call must NOT eject the user: the caller
   * renders its own empty state instead. Without this, any page that composes
   * endpoints with different perms breaks for roles that only partially hold
   * them (e.g. monitor viewing User Details: users:read yes, ratings:read no).
   * A `disabled` staff account still redirects even when this is set.
   */
  skipForbiddenRedirect?: boolean;
}

adminClient.interceptors.request.use(
  async (config) => {
    const deviceHeaders = deviceService.getDeviceHeaders();
    config.headers['Device-Id'] = deviceHeaders['Device-Id'];
    config.headers['Device-Type'] = deviceHeaders['Device-Type'];

    const authHeader = await getMemoryAuthHeader();
    if (authHeader) {
      config.headers.Authorization = authHeader;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

adminClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as RetriableConfig | undefined;
    const status: number | undefined = error.response?.status;

    // 401 = unauthenticated → refresh once → retry once → else login.
    if (status === 401 && originalRequest) {
      if (!originalRequest._retry && !isAuthEndpoint(originalRequest.url)) {
        originalRequest._retry = true;
        const token = await doRefresh();
        if (token) {
          originalRequest.headers = {
            ...(originalRequest.headers ?? {}),
            Authorization: `Bearer ${token}`,
          };
          return adminClient(originalRequest);
        }
      }
      // Refresh failed (or the auth endpoint itself rejected) → session dead.
      await clearMemorySession();
      redirectToLogin();
      return Promise.reject(error);
    }

    // 403 = authenticated but forbidden/disabled → route, never logout.
    // EXCEPT auth endpoints: backend login fails with generic 403 'Access denied'
    // and the login page owns that UX (generic toast, stays on form).
    if (status === 403 && !isAuthEndpoint(originalRequest?.url)) {
      const data = error.response?.data as
        | { disabled?: boolean; staff_disabled?: boolean; admin?: { status?: string } }
        | undefined;
      const disabled =
        data?.disabled === true ||
        data?.staff_disabled === true ||
        data?.admin?.status === 'disabled';

      // Optional panel: degrade inline. Disabled accounts always redirect.
      if (originalRequest?.skipForbiddenRedirect && !disabled) {
        return Promise.reject(error);
      }

      try {
        toast({ title: 'Action not permitted', variant: 'destructive' });
      } catch {
        // Toast unavailable (isolated tests) — redirect still applies.
      }
      adminRedirects.to(disabled ? '/admin/disabled' : '/admin/no-access');
    }

    return Promise.reject(error);
  },
);

/** True when the backend route is missing (probe fallback: 404/501). */
export function isBackendPendingError(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    return status === 404 || status === 501;
  }
  return false;
}

export interface AdminApi {
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
}

export const adminApi: AdminApi = {
  get: (url, config = {}) => adminClient.get(url, config),
  post: (url, data, config = {}) => adminClient.post(url, data, config),
  put: (url, data, config = {}) => adminClient.put(url, data, config),
  patch: (url, data, config = {}) => adminClient.patch(url, data, config),
  delete: (url, config = {}) => adminClient.delete(url, config),
};

export default adminClient;
